import { useState } from "react";
import { Send, X, Users } from "lucide-react";
import { useScrollFloatVisibility } from "@/hooks/useScrollFloatVisibility";

const TELEGRAM_NUMBER = "529371298761"; // +52 937 129 8761
const BUSINESS_NAME = "BHGC Nexus Logistics by El Cerrito de Tabasco SC de RL de CV";
const TELEGRAM_CHANNEL_URL = "https://t.me/bhgcnexus";

const QUICK_TOPICS = [
  "Quiero cotizar un envío",
  "Necesito ayuda con una guía",
  "Quiero levantar un reporte",
  "Tengo un problema con mi saldo",
  "Información sobre SaaS suscripciones",
  "Información sobre Franquicia White Label",
  "Información general"
];

function buildTelegramLink() {
  // Telegram abre el chat directo con el número, pero no soporta pre-cargar
  // el texto en un enlace de número (a diferencia de wa.me?text=).
  return `https://t.me/+${TELEGRAM_NUMBER}`;
}

export default function TelegramFloat() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const showFab = useScrollFloatVisibility(open);

  const send = (preset) => {
    const msg = preset || message || "Hola, necesito ayuda.";
    const fullName = name ? ` Mi nombre es ${name}.` : "";
    const finalMsg = `${msg}${fullName}`;
    // Copiamos el mensaje al portapapeles para que el usuario lo pegue en el
    // chat de Telegram (t.me/+<numero> no admite pre-cargar texto).
    if (navigator.clipboard) navigator.clipboard.writeText(finalMsg).catch(() => {});
    window.open(buildTelegramLink(), "_blank", "noopener,noreferrer");
    if (!preset) setOpen(false);
  };

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-[calc(8.5rem+env(safe-area-inset-bottom))] right-4 lg:right-6 lg:bottom-24 z-50 w-[min(92vw,360px)] surface shadow-2xl flex flex-col max-h-[70vh] animate-in fade-in slide-in-from-bottom-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink-border bg-[#0088CC]">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                <Send className="w-5 h-5 text-white" />
              </div>
              <div className="leading-tight">
                <div className="font-display font-bold text-white text-sm">Soporte BHGC Nexus</div>
                <div className="text-[0.65rem] text-white/80 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-[#29A9EB] rounded-full inline-block" /> En línea · Telegram
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-ink-inset dark:bg-[#0D1117]">
            <div className="text-[0.7rem] text-center text-ink-muted">Hoy</div>

            <div className="max-w-[85%] bg-ink-surface dark:bg-[#212C33] text-ink-text text-sm px-3 py-2.5 rounded-lg rounded-tl-none">
              ¡Hola! 👋 Bienvenido a <strong>BHGC Nexus Logistics</strong>.
              <br />¿En qué podemos ayudarte hoy? Selecciona una opción o escribe tu mensaje.
            </div>

            {/* Menú: enlace al canal de Telegram */}
            <a
              href={TELEGRAM_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 text-left text-xs px-3 py-2.5 border border-[#0088CC]/50 bg-[#0088CC]/10 text-ink-text hover:bg-[#0088CC]/20 rounded transition-colors"
            >
              <Users className="w-4 h-4 text-[#0088CC] shrink-0" />
              <span className="flex-1">
                <span className="block font-bold text-[#0088CC]">Unirse al canal de Telegram</span>
                <span className="block text-[0.65rem] text-ink-muted">Comunidad y novedades de BHGC Nexus</span>
              </span>
            </a>

            {/* Quick topics */}
            <div className="flex flex-col gap-2">
              {QUICK_TOPICS.map((t) => (
                <button
                  key={t}
                  onClick={() => send(t)}
                  className="text-left text-xs px-3 py-2 border border-cyan/40 text-cyan bg-cyan/5 hover:bg-cyan/10 rounded transition-colors"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Composer */}
          <div className="p-3 border-t border-ink-border bg-ink-surface space-y-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre (opcional)"
              className="w-full bg-ink border border-ink-border px-3 py-2 text-sm focus:border-cyan focus:outline-none"
            />
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder="Escribe tu mensaje…"
                className="flex-1 bg-ink border border-ink-border px-3 py-2 text-sm focus:border-cyan focus:outline-none"
              />
              <button
                onClick={() => send()}
                disabled={!message.trim()}
                className="bg-[#0088CC] hover:bg-[#007AB8] disabled:opacity-40 text-white px-3 py-2 transition-colors"
                aria-label="Enviar por Telegram"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[0.6rem] text-ink-muted text-center">
              Al enviar, se abrirá el chat de Telegram con BHGC Nexus (+52 937 129 8761) y copiaremos tu mensaje para que lo pegues.
            </p>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setOpen(v => !v)}
        aria-hidden={!showFab}
        className={`fixed bottom-[calc(7rem+env(safe-area-inset-bottom))] lg:bottom-6 right-[4.5rem] lg:right-[5rem] z-50 group transition-all duration-300 ${showFab ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
        aria-label={open ? "Cerrar chat de soporte" : "Abrir chat de soporte por Telegram"}
      >
        <span className="absolute inset-0 rounded-full bg-[#0088CC] animate-ping opacity-20" style={{ animationDuration: "2.4s" }} />
        <span className="relative w-12 h-12 rounded-full bg-[#0088CC] hover:bg-[#007AB8] flex items-center justify-center shadow-lg shadow-[#0088CC]/40 transition-colors">
          {open ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Send className="w-6 h-6 text-white" />
          )}
        </span>
      </button>
    </>
  );
}